// src/config/manuscriptElements.js
//
// The catalogue of manuscript structure elements — front matter, body matter and
// back matter — and the default template each one opens with.
//
// This is a catalogue, not state. The page reads it to know which elements exist,
// what order they sit in, which fields each one offers and what those fields say
// before the author types anything. The author's actual values live on the draft,
// under `manuscriptStructure.elements[id]`, saved by
// PUT /api/drafts/<id>/structure.
//
// `loc` says which tool owns the element. Two of the twenty-six — the title page
// and the colophon — are set in the Cover Designer rather than the Manuscript
// Editor, which is why the page has a mode toggle at all rather than one long
// list.
//
// That tab is labelled "Designer's Pages" and not "Cover Designer", which is the
// tool's own name. The distinction is the whole reason for the label: both
// elements are leaves printed INSIDE the book — the title page near the front,
// the colophon at the back — and neither one ever reaches the cover artwork.
// Named for the tool, the tab promised cover matter it does not hold, and
// readers of the page reasonably expected spine text and a back-cover blurb
// behind it. It says who sets these pages, not where they end up.
//
// `preview` names the shape the right-hand preview draws for that element. The
// preview renderers live with the preview column; the names are here because they
// are a property of the element, not of the renderer.
//
// Adding an element: give it an id that will never change (it is the storage key),
// an `order` that reflects where it falls in a finished book, and defaults that
// read as a worked example rather than lorem ipsum — the defaults are the first
// thing an author sees and they teach what the element is for.

export const CATEGORIES = {
  front: { id: 'front', label: 'Front Matter' },
  body: { id: 'body', label: 'Body Matter' },
  back: { id: 'back', label: 'Back Matter' },
};

export const LOCATIONS = {
  editor: { id: 'editor', label: 'Manuscript Editor' },
  cover: { id: 'cover', label: "Designer's Pages" },
};

// Elements whose content is centred on the page by convention — a dedication sits
// in the middle of an otherwise empty leaf, a chapter opening does not.
const CENTERED_PREVIEWS = ['centered', 'centered-quote', 'cover-front', 'cover-spec', 'authorbio'];

export const MANUSCRIPT_ELEMENTS = [
  // ---- FRONT MATTER ----
  {
    id: 'title-page',
    category: 'front',
    order: 1,
    name: 'Title Page',
    roles: ['Designer', 'Publisher'],
    purpose: "Displays the full title, subtitle, author, and publisher's imprint.",
    loc: 'cover',
    preview: 'cover-front',
    fields: [
      { key: 'title', label: 'Title', type: 'text', default: 'The Autumn of Livelihood' },
      { key: 'subtitle', label: 'Subtitle', type: 'text', default: 'A Novel' },
      { key: 'author', label: 'Author name', type: 'text', default: 'The Grandious Bambi' },
      { key: 'imprint', label: 'Publisher imprint', type: 'text', default: 'Revisionari Press' },
    ],
  },
  {
    id: 'copyright-page',
    category: 'front',
    order: 2,
    name: 'Copyright Page',
    roles: ['Publisher (Legal/Editorial)'],
    purpose: 'Contains legal notices, ISBN, edition info, and printing history.',
    loc: 'editor',
    preview: 'legal',
    fields: [
      { key: 'notice', label: 'Copyright notice', type: 'text', default: '© 2026 The Grandious Bambi' },
      { key: 'isbn', label: 'ISBN', type: 'text', default: '978-0-000000-0-0' },
      { key: 'edition', label: 'Edition', type: 'text', default: 'First Edition' },
      { key: 'history', label: 'Printing history', type: 'textarea', default: 'Printed in the United States of America' },
    ],
  },
  {
    id: 'dedication',
    category: 'front',
    order: 3,
    name: 'Dedication',
    roles: ['Author'],
    purpose: 'A personal note from the author, often to family or friends.',
    loc: 'editor',
    preview: 'centered',
    fields: [
      { key: 'text', label: 'Dedication text', type: 'textarea', default: 'For the turtles who never learned to swim.' },
    ],
  },
  {
    id: 'epigraph',
    category: 'front',
    order: 4,
    name: 'Epigraph',
    roles: ['Author'],
    purpose: 'A relevant quotation from another work to set the tone.',
    loc: 'editor',
    preview: 'centered-quote',
    fields: [
      { key: 'quote', label: 'Quotation', type: 'textarea', default: 'The tide does not ask permission.' },
      { key: 'source', label: 'Source attribution', type: 'text', default: 'Unknown, Sheridon Proverb' },
    ],
  },
  {
    id: 'toc',
    category: 'front',
    order: 5,
    name: 'Table of Contents',
    roles: ['Author', 'Designer', 'Production Editor'],
    purpose: 'Lists chapters and sections with page numbers.',
    loc: 'editor',
    preview: 'list',
    auto: true,
    fields: [
      {
        key: 'rows',
        label: 'Chapters',
        type: 'repeater',
        singular: 'chapter',
        subfields: [{ key: 'title', label: 'Title' }, { key: 'page', label: 'Page' }],
        default: [
          { title: 'Prologue', page: '1' },
          { title: 'Chapter One', page: '7' },
          { title: 'Chapter Two', page: '24' },
          { title: 'Chapter Three', page: '41' },
        ],
      },
    ],
  },
  {
    id: 'list-illustrations',
    category: 'front',
    order: 6,
    name: 'List of Illustrations / Tables',
    roles: ['Production Editor', 'Designer'],
    purpose: 'A separate list for figures or data tables.',
    loc: 'editor',
    preview: 'list',
    auto: true,
    fields: [
      {
        key: 'rows',
        label: 'Figures',
        type: 'repeater',
        singular: 'figure',
        subfields: [{ key: 'caption', label: 'Caption' }, { key: 'page', label: 'Page' }],
        default: [
          { caption: 'Fig. 1 — Map of Sheridon State', page: '12' },
          { caption: 'Fig. 2 — The Shop Floor', page: '38' },
        ],
      },
    ],
  },
  {
    id: 'foreword',
    category: 'front',
    order: 7,
    name: 'Foreword',
    roles: ['Guest Contributor (expert)', 'Acquisitions Editor'],
    purpose: 'Written by someone other than the author to lend credibility.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      { key: 'contributor', label: 'Contributor name', type: 'text', default: 'Dr. Elias Voss' },
      {
        key: 'body',
        label: 'Foreword text',
        type: 'textarea',
        default: 'When I first read this manuscript, I understood immediately why the turtles of Sheridon State stayed with me long after the final page.',
      },
    ],
  },
  {
    id: 'preface',
    category: 'front',
    order: 8,
    name: 'Preface',
    roles: ['Author'],
    purpose: 'Written by the author explaining the origin and purpose of the book.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      {
        key: 'body',
        label: 'Preface text',
        type: 'textarea',
        default: 'This book began as a question about what a community owes its own history, and what happens when that debt goes unpaid.',
      },
    ],
  },
  {
    id: 'acknowledgments-front',
    category: 'front',
    order: 9,
    name: 'Acknowledgments',
    roles: ['Author'],
    purpose: 'Thanks to people who helped create the book.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      {
        key: 'body',
        label: 'Acknowledgments text',
        type: 'textarea',
        default: 'My thanks to my editors, my early readers, and to Sheridon State, wherever it truly is.',
      },
    ],
  },
  {
    id: 'introduction',
    category: 'front',
    order: 10,
    name: 'Introduction',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'Leads directly into the main text; often the first "chapter" of content.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      {
        key: 'body',
        label: 'Introduction text',
        type: 'textarea',
        default: 'Every city has a rule it forgets it made. Sheridon State forgot slowly, then all at once.',
      },
    ],
  },

  // ---- BODY MATTER ----
  {
    id: 'prologue',
    category: 'body',
    order: 11,
    name: 'Prologue (Fiction)',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'An introductory scene set before the main story begins.',
    loc: 'editor',
    preview: 'chapter',
    chapterHeadingOnly: true,
    fields: [
      { key: 'heading', label: 'Heading', type: 'text', default: 'Prologue' },
      {
        key: 'body',
        label: 'Prologue text',
        type: 'textarea',
        default: "Fifty years before the boycott, the forefathers stood at the water's edge and warned a city that would not listen.",
      },
    ],
  },
  {
    id: 'parts-chapters',
    category: 'body',
    order: 12,
    name: 'Parts / Chapters',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'The primary structural units of the narrative or argument.',
    loc: 'editor',
    preview: 'chapter',
    fields: [
      { key: 'chapnum', label: 'Chapter number', type: 'text', default: 'Chapter One' },
      { key: 'chaptitle', label: 'Chapter title', type: 'text', default: 'The New Bigness' },
      {
        key: 'body',
        label: 'Chapter text',
        type: 'textarea',
        default: 'The turtles of the abhorrent city of Sheridon State were exhausted by the sheer amount of quantitative training they were immersed in.',
      },
    ],
  },
  {
    id: 'main-text',
    category: 'body',
    order: 13,
    name: 'Main Text / Narrative',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'The core content of the book.',
    loc: 'editor',
    preview: 'chapter',
    fields: [
      { key: 'chapnum', label: 'Chapter number', type: 'text', default: 'Chapter Two' },
      { key: 'chaptitle', label: 'Chapter title', type: 'text', default: 'Actor 1 in Shop' },
      {
        key: 'body',
        label: 'Chapter text',
        type: 'textarea',
        default: "All the children and parent turtles decided to boycott all their swimming lessons in protest of the city's enacted mandatory swimming lessons.",
      },
    ],
  },
  {
    id: 'epilogue',
    category: 'body',
    order: 14,
    name: 'Epilogue (Fiction)',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'A concluding section revealing the fate of characters after the main story.',
    loc: 'editor',
    preview: 'chapter',
    chapterHeadingOnly: true,
    fields: [
      { key: 'heading', label: 'Heading', type: 'text', default: 'Epilogue' },
      {
        key: 'body',
        label: 'Epilogue text',
        type: 'textarea',
        default: 'Years later, the shop still stood, and so did the sign: no swimming, no exceptions.',
      },
    ],
  },
  {
    id: 'conclusion',
    category: 'body',
    order: 15,
    name: 'Conclusion (Non-Fiction)',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'Summarizes main arguments and offers final thoughts.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      {
        key: 'body',
        label: 'Conclusion text',
        type: 'textarea',
        default: 'What Sheridon State lost was never the water. It was the skill of trusting what the forefathers already knew.',
      },
    ],
  },

  // ---- BACK MATTER ----
  {
    id: 'acknowledgments-back',
    category: 'back',
    order: 16,
    name: 'Acknowledgments (Alternate)',
    roles: ['Author'],
    purpose: 'If not placed in the front matter, it goes here.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      {
        key: 'body',
        label: 'Acknowledgments text',
        type: 'textarea',
        default: 'With thanks to everyone who read an early draft of this book and told me the truth.',
      },
    ],
  },
  {
    id: 'appendix',
    category: 'back',
    order: 17,
    name: 'Appendix / Appendices',
    roles: ['Author', 'Production Editor'],
    purpose: 'Supplementary material (charts, documents, data) supporting the text.',
    loc: 'editor',
    preview: 'bodytext',
    fields: [
      { key: 'title', label: 'Appendix title', type: 'text', default: 'Appendix A: The Sheridon Swimming Ordinance' },
      {
        key: 'body',
        label: 'Appendix content',
        type: 'textarea',
        default: 'Full text of the original ordinance, reproduced here for reference.',
      },
    ],
  },
  {
    id: 'endnotes',
    category: 'back',
    order: 18,
    name: 'Endnotes',
    roles: ['Author', 'Copy Editor'],
    purpose: 'Source citations and explanatory notes, organized by chapter.',
    loc: 'editor',
    preview: 'list',
    auto: true,
    fields: [
      {
        key: 'rows',
        label: 'Notes',
        type: 'repeater',
        singular: 'note',
        subfields: [{ key: 'note', label: 'Note' }, { key: 'chapter', label: 'Chapter' }],
        default: [
          { note: 'On the original ordinance', chapter: 'Ch. 1' },
          { note: 'Population estimate, Sheridon Census', chapter: 'Ch. 3' },
        ],
      },
    ],
  },
  {
    id: 'bibliography',
    category: 'back',
    order: 19,
    name: 'Bibliography / References',
    roles: ['Author', 'Copy Editor'],
    purpose: 'Formal list of sources cited, following a specific style guide.',
    loc: 'editor',
    preview: 'list',
    auto: true,
    fields: [
      {
        key: 'rows',
        label: 'Sources',
        type: 'repeater',
        singular: 'source',
        subfields: [{ key: 'source', label: 'Source' }, { key: 'year', label: 'Year' }],
        default: [
          { source: 'Sheridon State Municipal Archive', year: '1974' },
          { source: 'Voss, E. On Mandatory Aquatics', year: '2011' },
        ],
      },
    ],
  },
  {
    id: 'index',
    category: 'back',
    order: 20,
    name: 'Index',
    roles: ['Professional Indexer', 'Production Editor'],
    purpose: 'Alphabetized list of subjects and names with page numbers.',
    loc: 'editor',
    preview: 'list',
    auto: true,
    fields: [
      {
        key: 'rows',
        label: 'Terms',
        type: 'repeater',
        singular: 'term',
        subfields: [{ key: 'term', label: 'Term' }, { key: 'page', label: 'Page' }],
        default: [
          { term: 'boycott, origins of', page: '44' },
          { term: 'forefathers, warnings of', page: '12' },
          { term: 'Sheridon State, founding of', page: '3' },
        ],
      },
    ],
  },
  {
    id: 'glossary',
    category: 'back',
    order: 21,
    name: 'Glossary',
    roles: ['Author', 'Developmental Editor'],
    purpose: 'Alphabetized list of specialized terms and definitions.',
    loc: 'editor',
    preview: 'glossary',
    fields: [
      {
        key: 'rows',
        label: 'Terms',
        type: 'repeater',
        singular: 'term',
        subfields: [{ key: 'term', label: 'Term' }, { key: 'definition', label: 'Definition' }],
        default: [
          { term: 'Forefathers', definition: 'The founding generation of Sheridon State.' },
          { term: 'Boycott', definition: "The turtles' collective refusal of mandatory lessons." },
        ],
      },
    ],
  },
  {
    id: 'about-author',
    category: 'back',
    order: 22,
    name: 'About the Author',
    roles: ['Author', 'Marketing'],
    purpose: 'Short biography of the writer, often with a photo.',
    loc: 'editor',
    preview: 'authorbio',
    fields: [
      {
        key: 'bio',
        label: 'Author bio',
        type: 'textarea',
        default: 'The Grandious Bambi lives and writes near the coast, where the water rules are, thankfully, less strict.',
      },
    ],
  },
  {
    id: 'reading-guide',
    category: 'back',
    order: 23,
    name: 'Reading Group Guide',
    roles: ['Marketing', 'Author'],
    purpose: 'Discussion questions and Q&A for book clubs.',
    loc: 'editor',
    preview: 'guide',
    fields: [
      {
        key: 'rows',
        label: 'Questions',
        type: 'repeater',
        singular: 'question',
        subfields: [{ key: 'text', label: 'Question' }],
        default: [
          { text: 'What does the boycott represent to each generation of turtles?' },
          { text: "Why do the forefathers' warnings go unheeded for fifty years?" },
        ],
      },
    ],
  },
  {
    id: 'colophon',
    category: 'back',
    order: 24,
    name: 'Colophon',
    roles: ['Designer', 'Production Editor'],
    purpose: 'Brief statement describing the typeface and printing methods.',
    loc: 'cover',
    preview: 'cover-spec',
    fields: [
      { key: 'typeface', label: 'Typeface name', type: 'text', default: 'Source Serif 4' },
      { key: 'notes', label: 'Printing notes', type: 'textarea', default: 'Set on acid-free paper, hardcover binding.' },
    ],
  },
  {
    id: 'also-by',
    category: 'back',
    order: 25,
    name: 'Also by the Author',
    roles: ['Publisher', 'Marketing'],
    purpose: 'A list of other books written by the same author.',
    loc: 'editor',
    preview: 'booklist',
    fields: [
      { key: 'blurb', label: 'Section blurb', type: 'text', default: 'Also by The Grandious Bambi' },
      {
        key: 'rows',
        label: 'Titles',
        type: 'repeater',
        singular: 'title',
        subfields: [{ key: 'title', label: 'Title' }],
        default: [{ title: 'The Longer Tide' }, { title: 'What the Forefathers Knew' }],
      },
    ],
  },
  {
    id: 'advertisements',
    category: 'back',
    order: 26,
    name: 'Advertisements / Catalogue',
    roles: ['Marketing'],
    purpose: 'Promotions for other books by the same publisher (rare in modern hardcovers).',
    loc: 'editor',
    preview: 'booklist',
    fields: [
      { key: 'blurb', label: 'Section blurb', type: 'text', default: 'More from Revisionari Press' },
      {
        key: 'rows',
        label: 'Titles',
        type: 'repeater',
        singular: 'title',
        subfields: [{ key: 'title', label: 'Title' }],
        default: [{ title: 'The Kinfolk Home' }, { title: 'Live Beautiful' }, { title: 'The New Nenumu' }],
      },
    ],
  },
];

export const ELEMENTS_BY_ID = MANUSCRIPT_ELEMENTS.reduce((acc, el) => {
  acc[el.id] = el;
  return acc;
}, {});

/** Elements owned by one tool, in book order. */
export function elementsForLocation(loc) {
  return MANUSCRIPT_ELEMENTS.filter((el) => el.loc === loc);
}

/** Elements in one category, within the Manuscript Editor. */
export function elementsForCategory(category) {
  return MANUSCRIPT_ELEMENTS.filter((el) => el.category === category && el.loc === 'editor');
}

/** The default position for an element, before the author moves it. */
export function defaultPosition(element) {
  return CENTERED_PREVIEWS.includes(element.preview) ? 'center' : 'top';
}

/**
 * The default formatting for an element.
 *
 * Source Serif 4 at 16px, because this is book interior text and the app already
 * loads that face. The author changes it from the toolbar — the font list is the
 * whole of `src/config/fonts.js`, not a house style, because this page is where
 * they design the book.
 *
 * Alignment follows the element: the things that sit centred on an otherwise
 * empty leaf (a dedication, an epigraph, a title page) open centred, everything
 * else ranges left. Without the element they range left.
 */
export function defaultFormat(element) {
  return {
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    align: element && CENTERED_PREVIEWS.includes(element.preview) ? 'center' : 'left',
    fontFamily: 'Source Serif 4',
    fontSize: 16,
    textStyle: '',
  };
}

/** The default field values for one element — a deep copy, safe to mutate. */
export function defaultFields(element) {
  const fields = {};
  element.fields.forEach((f) => {
    fields[f.key] = f.type === 'repeater'
      ? (f.default || []).map((row) => ({ ...row }))
      : (f.default || '');
  });
  return fields;
}

/**
 * One element's full default state, as stored under `manuscriptStructure`.
 *
 * **Elements start excluded.** A book has the sections its author chose, not all
 * twenty-six — and since the toggle now decides what is printed, defaulting to on
 * would put a page of placeholder dedication into every exported PDF.
 */
export function defaultElementState(element) {
  return {
    enabled: false,
    position: defaultPosition(element),
    format: defaultFormat(element),
    fields: defaultFields(element),
  };
}

/**
 * Every element's default state, keyed by id.
 *
 * The page starts here and then overlays whatever the draft has saved, so an
 * element added to this catalogue after an author last saved appears with its
 * defaults rather than as a gap.
 */
export function defaultStructureState() {
  const state = {};
  MANUSCRIPT_ELEMENTS.forEach((el) => {
    state[el.id] = defaultElementState(el);
  });
  return state;
}
